import { PORTFOLIO_REFETCH_POLICY } from 'config/RefetchPolicies'
import { ZERO_ADDRESS } from 'constants/misc'
import { unsupportedSwapTokens } from 'constants/unsupportedSwapTokens'
import { useConnection } from 'hooks/useConnection'
import { CurrencyWithBalanceAndPrice, useTokensWithBalanceAndPriceFast } from 'hooks/useTokensWithBalanceAndPriceFast'
import useCurrencyBalance from 'lib/hooks/useCurrencyBalance'
import useNativeCurrency from 'lib/hooks/useNativeCurrency'
import { useMemo } from 'react'
import { WrappedTokenInfo } from 'state/lists/wrappedTokenInfo'
import { Token } from 'types/portfolio'
import { formatNumber } from 'utils/formatCurrencyAmount'
import { filterTokenHoldings } from 'utils/portfolio/filterHelpers'
import { Address } from 'viem'

function formatTokenData(address: Address, token: Token, balance: string, decimals: number, price: number) {
  const balanceValue = parseFloat(balance) / Math.pow(10, decimals)
  const balanceUSD = balanceValue * price

  return {
    address,
    token,
    balance,
    balanceValue,
    balanceFormatted: formatNumber(balanceValue) || '0',
    balanceUSD,
    balanceUSDFormatted: `$${formatNumber(balanceUSD, { mantissa: 2 }) || '0.00'}`,
    price,
    priceFormatted: `$${formatNumber(price, { mantissa: price < 1 ? 4 : 2 }) || '0.00'}`,
    decimals,
  }
}

function toPortfolioToken(currencyWithData: CurrencyWithBalanceAndPrice): Token {
  const wrappedCurrency = currencyWithData.currency.wrapped as WrappedTokenInfo

  return {
    id: wrappedCurrency.address,
    address: wrappedCurrency.address as Address,
    name: currencyWithData.currency.name ?? wrappedCurrency.name,
    symbol: currencyWithData.currency.symbol ?? wrappedCurrency.symbol,
    decimals: wrappedCurrency.decimals,
    logoUri: wrappedCurrency.logoURI,
  }
}

export function useWalletPortfolioValue(
  tokenAddresses?: Address[],
  options?: {
    enabled?: boolean
    filter?: string
    enableLpTokens?: boolean
  }
) {
  const { account } = useConnection()

  const { tokensWithData, isLoading } = useTokensWithBalanceAndPriceFast({
    refetchPolicy: {
      ...PORTFOLIO_REFETCH_POLICY,
      enabled: options?.enabled !== false,
    },
    enableLpTokens: options?.enableLpTokens ?? false,
    exclude: unsupportedSwapTokens,
  })

  const native = useNativeCurrency()
  const nativeBalance = useCurrencyBalance(account ?? undefined, native, PORTFOLIO_REFETCH_POLICY)
  const addressSet = useMemo(
    () =>
      tokenAddresses && tokenAddresses.length > 0
        ? new Set(tokenAddresses.map((address) => address.toLowerCase()))
        : null,
    [tokenAddresses]
  )

  let balancesWithPrices = tokensWithData
    .filter((item) => (addressSet ? addressSet.has(item.currency.wrapped.address.toLowerCase()) : true))
    .map((item) => {
      const portfolioToken = toPortfolioToken(item)
      return formatTokenData(
        portfolioToken.address as Address,
        portfolioToken,
        item.balance.quotient.toString(),
        portfolioToken.decimals,
        item.price
      )
    })

  if (native && native.symbol && native.name) {
    const wrappedNativeAddress = native.wrapped.address.toLowerCase()
    const nativePrice =
      tokensWithData.find(
        (tokenWithData) => tokenWithData.currency.wrapped.address.toLowerCase() === wrappedNativeAddress
      )?.price || 0
    const nativeToken: Token = {
      id: 'native',
      address: ZERO_ADDRESS,
      name: native.name,
      symbol: native.symbol,
      decimals: native.decimals,
      logoUri: `https://static.kodiak.finance/tokens/${native.name.toLowerCase()}.png`,
    }
    const nativeData = formatTokenData(
      ZERO_ADDRESS as Address,
      nativeToken,
      nativeBalance?.quotient.toString() ?? '0',
      native.decimals,
      nativePrice
    )

    if (nativeData.balanceValue > 0) {
      balancesWithPrices = [nativeData, ...balancesWithPrices]
    }
  }

  balancesWithPrices = balancesWithPrices.filter((item) => item.balanceValue > 0)

  const filteredTokens = filterTokenHoldings(balancesWithPrices, options?.filter || '')
  const totalValue = balancesWithPrices.reduce((sum, item) => sum + item.balanceUSD, 0)
  const filteredTotalValue = filteredTokens.reduce((sum, item) => sum + item.balanceUSD, 0)
  const tokenCount = balancesWithPrices.length
  const filteredTokenCount = filteredTokens.length

  return {
    data: {
      totalValue,
      filteredTotalValue,
      tokenCount,
      filteredTokenCount,
      allTokens: balancesWithPrices,
      tokens: filteredTokens,
    },
    isLoading,
  }
}
